// src/components/OpeningCheckListView.jsx
import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import SignatureSvg from './SignatureSvg';
import { SANDART_OPENING_SECTIONS, SANDART_OPENING_KEYS, isSandartRecord } from '../data/sandartChecklists';

const SECTIONS = [
  {
    key: 'general', title: 'General Opening', emoji: '🔑',
    items: [
      { key: 'extension_connected', label: 'Connect extension (listen for "camera ready")' },
      { key: 'clock_in_done',       label: 'Clock in — send message with resort + arrival time' },
      { key: 'envelope_written',    label: 'Write resort name and date on the back of an envelope' },
      { key: 'premades_counted',    label: 'Count pre-mades, combs and clips — add to system' },
      { key: 'waiting_list_copies', label: 'Check copies (waiting list + tattoo care instructions)' },
      { key: 'materials_checked',   label: 'Check materials — report damaged/missing immediately' },
    ],
  },
  {
    key: 'hygiene', title: 'Hygiene & Equipment', emoji: '🧴',
    items: [
      { key: 'disinfectant_added',  label: 'Add disinfectant (1–2 caps) to the acrylic container' },
      { key: 'ink_bottles_checked', label: 'Check ink bottles (both holes unclogged)' },
      { key: 'tattoo_gun_checked',  label: 'Test tattoo gun (alcohol + ink on paper)' },
      { key: 'glitter_checked',     label: 'Check glitter paste' },
      { key: 'mirror_checked',      label: 'Check mirror (clean, not broken)' },
      { key: 'doll_checked',        label: "Check doll's face (clean, no pen marks)" },
    ],
  },
  {
    key: 'technology', title: 'Technology', emoji: '📱',
    items: [
      { key: 'printer_checked',     label: 'Printer working, charged and has paper' },
      { key: 'tablet_wifi_checked', label: 'Tablet connected to kiosk Wi-Fi (Franklin)' },
      { key: 'report_issues',       label: 'Report any pending issues from previous day' },
    ],
  },
  {
    key: 'physical', title: 'Physical Setup', emoji: '🏖️',
    items: [
      { key: 'doors_opened',   label: 'Open all doors (all poles secure)' },
      { key: 'banners_placed', label: 'Hang banners and place sign at the front' },
      { key: 'umbrella_set',   label: 'Set up umbrella BEFORE opening doors' },
      { key: 'ready_by_11am', label: 'All doors open and ready by 11am ✅' },
    ],
  },
];

const ALL_KEYS = SECTIONS.flatMap(s => s.items.map(i => i.key));

const OpeningCheckListView = React.forwardRef(({ data }, ref) => {
  if (!data) return <Typography>Loading...</Typography>;

  // Hotel Sand Art usa outra lista de itens; o resto da tela é igual.
  const sandart = isSandartRecord(data);
  const sections = sandart ? SANDART_OPENING_SECTIONS : SECTIONS;
  const allKeys = sandart ? SANDART_OPENING_KEYS : ALL_KEYS;

  const { date, name, resort, notes, completed_at, signature_svg } = data;

  const base = { fontFamily: '"Arial Narrow", Arial, sans-serif', fontSize: '8.5pt', color: '#111' };

  const sectionTitle = {
    ...base, fontWeight: 'bold', fontSize: '9pt',
    backgroundColor: '#D3D3D3', padding: '2px 6px',
    marginTop: '6px', marginBottom: '2px', borderLeft: '3px solid #16a34a',
  };

  const Checkbox = ({ checked }) => (
    <Box sx={{
      width: '12px', height: '12px', border: '1.5px solid #555', borderRadius: '2px',
      backgroundColor: checked ? '#16a34a' : '#fff', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {checked && (
        <svg width="8" height="8" viewBox="0 0 10 10">
          <polyline points="1,5 4,8 9,2" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
      )}
    </Box>
  );

  const totalChecked = allKeys.filter(k => data[k]).length;
  const missing = sections.flatMap(s => s.items.filter(i => !data[i.key]).map(i => i.label));

  return (
    <Box ref={ref} sx={{
      padding: '12px', backgroundColor: 'white', width: '780px',
      fontFamily: '"Arial Narrow", Arial, sans-serif', color: 'black',
      border: '1px solid black', boxSizing: 'border-box',
    }}>
      <Typography variant="h5" sx={{
        textAlign: 'center', fontWeight: 'bold', mb: 1, fontSize: '14pt',
        fontFamily: '"Arial Black", Gadget, sans-serif', textTransform: 'uppercase',
      }}>
        {sandart ? 'SAND ART OPENING CHECKLIST' : 'OPENING CHECKLIST'}
      </Typography>

      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '10pt', mb: 0.3 }}><b>DATE:</b> {date}</Typography>
        <Typography sx={{ fontSize: '10pt', mb: 0.3 }}><b>NAME:</b> {name}</Typography>
        <Typography sx={{ fontSize: '10pt', mb: 0.3 }}><b>RESORT:</b> {resort}</Typography>
        {completed_at && <Typography sx={{ fontSize: '9pt', color: '#555' }}><b>Signed at:</b> {completed_at}</Typography>}
      </Box>

      <Box sx={{ borderTop: '1.5px solid #555', mb: 1 }} />

      {/* 2 colunas lado a lado */}
      <Grid container spacing={1}>
        {sections.map((section) => {
          const checkedCount = section.items.filter(i => data[i.key]).length;
          return (
            <Grid item xs={6} key={section.key}>
              <Box sx={sectionTitle}>
                {section.emoji} {section.title} ({checkedCount}/{section.items.length})
              </Box>
              {section.items.map((item) => {
                const checked = !!data[item.key];
                return (
                  <Box key={item.key} sx={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '2px 6px', borderBottom: '1px solid #f0f0f0', minHeight: '20px',
                    backgroundColor: checked ? '#f0fdf4' : '#fff',
                  }}>
                    <Checkbox checked={checked} />
                    <Typography sx={{ ...base, color: checked ? '#166534' : '#374151', flex: 1 }}>
                      {item.label}
                    </Typography>
                    {!checked && <Typography sx={{ fontSize: '7pt', color: '#ef4444', fontWeight: 'bold' }}>✗</Typography>}
                  </Box>
                );
              })}
            </Grid>
          );
        })}
      </Grid>

      {/* Resumo */}
      <Box sx={{ mt: 1.5, borderTop: '1px solid #ccc', pt: 1 }}>
        <Typography sx={{ ...base, fontWeight: 'bold', fontSize: '9pt' }}>
          SUMMARY: {totalChecked}/{allKeys.length} items completed
          {totalChecked === allKeys.length ? ' ✅ ALL DONE' : ` — ${allKeys.length - totalChecked} item(s) not checked`}
        </Typography>
        {missing.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <Typography sx={{ ...base, fontWeight: 'bold', color: '#b91c1c', fontSize: '8pt' }}>NOT CHECKED:</Typography>
            {missing.map((label, i) => (
              <Typography key={i} sx={{ ...base, fontSize: '8pt', color: '#b91c1c', pl: 1 }}>• {label}</Typography>
            ))}
          </Box>
        )}
      </Box>

      {notes && (
        <Box sx={{ mt: 1, borderTop: '1px solid #ccc', pt: 0.5 }}>
          <Typography sx={{ ...base, fontWeight: 'bold', fontSize: '9pt' }}>NOTES:</Typography>
          <Typography sx={{ ...base, pl: 1 }}>{notes}</Typography>
        </Box>
      )}

      {/* Assinatura */}
      <Box sx={{ mt: 1.5, borderTop: '1.5px solid #555', pt: 1 }}>
        <Typography sx={{ ...base, fontWeight: 'bold', fontSize: '9pt', mb: 0.5 }}>EMPLOYEE SIGNATURE:</Typography>
        <SignatureSvg signatureSvgJson={signature_svg} width={400} height={100} />
        <Typography sx={{ ...base, fontSize: '8pt', color: '#555', mt: 0.5 }}>
          By signing, I confirm that I completed all opening checklist items truthfully and that the kiosk was ready for operation.
        </Typography>
      </Box>
    </Box>
  );
});

export default OpeningCheckListView;